import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, homePathForRole } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import TeacherDashboard from "@/pages/TeacherDashboard";
import ClassPassportTable from "@/pages/ClassPassportTable";
import BulkColors from "@/pages/BulkColors";
import StudentProfile from "@/pages/StudentProfile";
import StudentManagement from "@/pages/StudentManagement";
import ParentHome from "@/pages/ParentHome";
import SchoolCalendar from "@/pages/SchoolCalendar";
import ParentSettings from "@/pages/ParentSettings";
import ParentSummary from "@/pages/ParentSummary";
import EmailTemplate from "@/pages/EmailTemplate";
import ClassDashboard from "@/pages/ClassDashboard";
import TrashPage from "@/pages/TrashPage";
import ArchivedClasses from "@/pages/ArchivedClasses";
import OnboardingWizard from "@/pages/OnboardingWizard";
import FirstPasswordChange from "@/pages/FirstPasswordChange";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TeacherSettings from "@/pages/TeacherSettings";
import RentreeWizard from "@/pages/RentreeWizard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminTeachers from "@/pages/admin/AdminTeachers";
import AdminParents from "@/pages/admin/AdminParents";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";

function FullScreenLoader() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#FAFAFA] dark:bg-[#0A0A0A]">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
    </div>
  );
}

// Réservé aux utilisateurs connectés, avec vérification optionnelle du rôle.
function ProtectedRoute({ role, children, allowFirstLogin = false, allowOnboarding = false }) {
  const { user } = useAuth();
  if (user === undefined) return <FullScreenLoader />;
  if (user === null) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={homePathForRole(user.role)} replace />;
  if (user.role === "parent" && user.must_change_password && !allowFirstLogin)
    return <Navigate to="/parent/definir-mot-de-passe" replace />;
  if (user.role === "teacher" && user.onboarding_completed === false && !allowOnboarding)
    return <Navigate to="/bienvenue" replace />;
  return children;
}

// Redirige la racine selon l'état d'authentification.
function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) return <FullScreenLoader />;
  if (user === null) return <Navigate to="/login" replace />;
  return <Navigate to={homePathForRole(user.role)} replace />;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === undefined) return <FullScreenLoader />;
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/inscription" element={<Register />} />
            <Route path="/verifier-email" element={<VerifyEmail />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reinitialiser" element={<ResetPassword />} />
            <Route
              path="/bienvenue"
              element={
                <ProtectedRoute role="teacher" allowOnboarding>
                  <OnboardingWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/parametres"
              element={
                <ProtectedRoute role="teacher">
                  <TeacherSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/rentree"
              element={
                <ProtectedRoute role="teacher">
                  <RentreeWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant"
              element={
                <ProtectedRoute role="teacher">
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/classe/:id"
              element={
                <ProtectedRoute role="teacher">
                  <ClassPassportTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/classe/:id/couleurs-collectives"
              element={
                <ProtectedRoute role="teacher">
                  <BulkColors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/classe/:id/eleves"
              element={
                <ProtectedRoute role="teacher">
                  <StudentManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/eleve/:studentId"
              element={
                <ProtectedRoute role="teacher">
                  <StudentProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/calendrier"
              element={
                <ProtectedRoute role="teacher">
                  <SchoolCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/modele-email"
              element={
                <ProtectedRoute role="teacher">
                  <EmailTemplate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/classe/:id/tableau-de-bord"
              element={
                <ProtectedRoute role="teacher">
                  <ClassDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/corbeille"
              element={
                <ProtectedRoute role="teacher">
                  <TrashPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enseignant/archives"
              element={
                <ProtectedRoute role="teacher">
                  <ArchivedClasses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent"
              element={
                <ProtectedRoute role="parent">
                  <ParentHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/definir-mot-de-passe"
              element={
                <ProtectedRoute role="parent" allowFirstLogin>
                  <FirstPasswordChange />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/resume"
              element={
                <ProtectedRoute role="parent">
                  <ParentSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/parametres"
              element={
                <ProtectedRoute role="parent">
                  <ParentSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={<ProtectedRoute role="super_admin"><AdminDashboard /></ProtectedRoute>}
            />
            <Route
              path="/admin/ecoles"
              element={<ProtectedRoute role="super_admin"><AdminSchools /></ProtectedRoute>}
            />
            <Route
              path="/admin/enseignants"
              element={<ProtectedRoute role="super_admin"><AdminTeachers /></ProtectedRoute>}
            />
            <Route
              path="/admin/parents"
              element={<ProtectedRoute role="super_admin"><AdminParents /></ProtectedRoute>}
            />
            <Route
              path="/admin/eleves"
              element={<ProtectedRoute role="super_admin"><AdminStudents /></ProtectedRoute>}
            />
            <Route
              path="/admin/journal"
              element={<ProtectedRoute role="super_admin"><AdminAudit /></ProtectedRoute>}
            />
            <Route
              path="/admin/parametres"
              element={<ProtectedRoute role="super_admin"><AdminSettings /></ProtectedRoute>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
